import { hasTeamRole, type ApiToken, type CreatedToken, type TokenCapability } from '@greenroom/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Copy } from 'lucide-react'
import { useState, type FormEvent } from 'react'
import { toast } from 'sonner'
import { api, errorMessage } from '../lib/api'
import { useUser } from '../lib/auth'
import { Button } from './ui/button'
import { Dialog } from './ui/dialog'
import { Field } from './ui/field'
import { ErrorNote } from './ui/misc'
import { Select } from './ui/select'
import { Switch } from './ui/switch'

const tokenQuery = { queryKey: ['account-tokens'], queryFn: () => api<ApiToken[]>('/api/account/tokens') }
const date = (s: string) => new Date(s).toLocaleDateString()

export function TokensDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const qc = useQueryClient()
  const me = useUser()
  const producer = me.teams.some((t) => hasTeamRole(t.role, 'producer'))
  const tokens = useQuery({ ...tokenQuery, enabled: open })
  const [label, setLabel] = useState('')
  const [days, setDays] = useState('30')
  const [edit, setEdit] = useState(false)
  const [download, setDownload] = useState(false)
  const [issued, setIssued] = useState<string | null>(null)
  const create = useMutation({
    mutationFn: () => {
      const capabilities: TokenCapability[] = ['read']
      if (edit) capabilities.push('edit')
      if (download) capabilities.push('download')
      return api<CreatedToken>('/api/account/tokens', { body: { label, days: Number(days), capabilities } })
    },
    onSuccess: ({ token, info }) => {
      setIssued(token)
      setLabel('')
      qc.setQueryData(tokenQuery.queryKey, (old: ApiToken[] | undefined) => [info, ...old ?? []])
    },
  })
  const revoke = useMutation({
    mutationFn: (id: string) => api<ApiToken>(`/api/account/tokens/${id}`, { method: 'DELETE' }),
    onSuccess: (info) => qc.setQueryData(tokenQuery.queryKey, (old: ApiToken[] | undefined) => old?.map((t) => t.id === info.id ? info : t)),
  })
  const close = (value: boolean) => {
    onOpenChange(value)
    if (!value) { setIssued(null); create.reset(); setLabel(''); setEdit(false); setDownload(false); setDays('30') }
  }
  const copy = async (value: string) => {
    try { await navigator.clipboard.writeText(value); toast.success('Copied') }
    catch { toast.error('Could not copy. Select and copy the text instead.') }
  }
  const submit = (event: FormEvent) => { event.preventDefault(); setIssued(null); create.mutate() }
  return (
    <Dialog open={open} onOpenChange={close} title="Access tokens" description="Connect an agent to your teams. Reading is always enabled; editing and media downloads are opt-in." size="lg">
      <div className="space-y-6">
        {issued && <div className="space-y-3 rounded-xl border border-accent/40 bg-accent-soft p-4">
          <p className="text-sm font-medium">Copy your token now. It is shown only once.</p>
          <div className="flex items-start gap-2">
            <code aria-label="New access token" className="min-w-0 flex-1 break-all rounded-lg bg-surface p-2 text-xs select-all">{issued}</code>
            <Button size="icon" variant="ghost" aria-label="Copy token" onClick={() => void copy(issued)}><Copy className="size-4" /></Button>
          </div>
          <p className="text-xs text-muted">Set GREENROOM_URL and GREENROOM_TOKEN in your agent environment, then add the MCP server:</p>
          <code className="block overflow-x-auto rounded-lg bg-surface p-2 text-xs select-all">claude mcp add greenroom -- node /path/to/greenroom.mjs mcp</code>
        </div>}
        <form onSubmit={submit} className="space-y-4">
          <Field label="Token label" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="claude mac" maxLength={40} pattern="[a-z0-9 ._\-]+" hint="Up to 40 lowercase letters, numbers, spaces, dots, underscores or hyphens." required />
          <div className="space-y-1.5">
            <span className="field-label">Expires in</span>
            <Select label="Token expiry" value={days} onChange={setDays} options={['7', '30', '90'].map((value) => ({ value, label: `${value} days` }))} />
          </div>
          <Switch label="Edit (producer)" hint="Apply revertible edits, import recordings and start exports." checked={edit} onChange={setEdit} disabled={!producer} />
          <Switch label="Download media" hint="Allow media to leave Greenroom." checked={download} onChange={setDownload} disabled={!producer} />
          {create.error && <ErrorNote>{errorMessage(create.error)}</ErrorNote>}
          <div className="flex justify-end"><Button type="submit" variant="primary" loading={create.isPending} disabled={!label.trim()}>Create token</Button></div>
        </form>
        <div className="space-y-3 border-t border-border pt-4">
          <h3 className="text-sm font-semibold">Your tokens</h3>
          {tokens.isPending && <p className="text-sm text-muted">Loading tokens…</p>}
          {tokens.error && <ErrorNote>{errorMessage(tokens.error)}</ErrorNote>}
          {tokens.data?.length === 0 && <p className="text-sm text-muted">No access tokens yet.</p>}
          {tokens.data?.map((t) => <div key={t.id} className="flex items-start justify-between gap-3 rounded-lg border border-border p-3">
            <div className="min-w-0 space-y-1 text-sm">
              <p className="break-words font-medium">{t.label} <code className="text-xs font-normal text-faint">{t.prefix}…</code></p>
              <p className="text-xs text-muted">{t.capabilities.join(' · ')} · {t.revokedAt ? 'Revoked' : Date.parse(t.expiresAt) <= Date.now() ? 'Expired' : `Expires ${date(t.expiresAt)}`}</p>
              <p className="text-xs text-faint">{t.lastUsedAt ? `Last used ${new Date(t.lastUsedAt).toLocaleString()}` : 'Never used'}</p>
            </div>
            {!t.revokedAt && <Button size="sm" variant="ghost" loading={revoke.isPending && revoke.variables === t.id} onClick={() => revoke.mutate(t.id)} aria-label={`Revoke ${t.label}`}>Revoke</Button>}
          </div>)}
        </div>
      </div>
    </Dialog>
  )
}
